import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Power, MapPin, Flag, Bike, Clock, Check, X, KeyRound, ArrowRight, Package, Phone, LogOut as ReleaseIcon, Wallet, PackageCheck, Navigation, AlertTriangle,
} from "lucide-react";
import { rpc, supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Card, Loader } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { StatusTimeline } from "@/components/StatusTimeline";
import { formatCOP, formatKm, CARGO_CATEGORY_LABELS } from "@/lib/format";

const NEXT = {
  ACCEPTED: { to: "DRIVER_GOING_TO_PICKUP", label: "Voy a recoger" },
  DRIVER_GOING_TO_PICKUP: { to: "DRIVER_ARRIVED_PICKUP", label: "Llegué al origen" },
  DRIVER_ARRIVED_PICKUP: { to: "PICKED_UP", label: "Recogí el paquete" },
  PICKED_UP: { to: "IN_TRANSIT", label: "Iniciar tránsito" },
  IN_TRANSIT: { to: "DRIVER_ARRIVED_DESTINATION", label: "Llegué al destino" },
};

function Countdown({ expiresAt }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.round((new Date(expiresAt) - new Date()) / 1000)));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [expiresAt]);
  return (
    <div className="grid h-16 w-16 place-items-center rounded-full border-4 border-[#FF6A00] bg-[#18181B] font-heading text-xl font-extrabold text-[#FF6A00]" data-testid="offer-countdown">
      {left}
    </div>
  );
}

export default function DriverDashboard() {
  const { profile, refreshProfile } = useAuth();
  const [available, setAvailable] = useState(!!profile?.is_available);
  const [offers, setOffers] = useState([]);
  const [current, setCurrent] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const approved = profile?.driver_approval === "APPROVED";
  const codeRef = useRef();

  const load = useCallback(async () => {
    try {
      const [cur, ofs, earn] = await Promise.all([
        rpc("get_driver_current_service"),
        rpc("get_my_offers"),
        rpc("get_driver_earnings"),
      ]);
      setCurrent(cur);
      setOffers(ofs || []);
      setEarnings(earn || null);
    } catch (e) {
      /* noop */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  // Realtime: nuevas ofertas / cambios aparecen sin recargar (RLS limita a driver_id = auth.uid())
  useEffect(() => {
    if (!profile?.id) return;
    const ch = supabase
      .channel(`driver-offers-${profile.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "service_offers", filter: `driver_id=eq.${profile.id}` },
        () => load()
      )
      .subscribe();
    const onVisible = () => document.visibilityState === "visible" && load();
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      supabase.removeChannel(ch);
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [profile?.id, load]);

  useEffect(() => setAvailable(!!profile?.is_available), [profile]);

  const toggle = async () => {
    const next = !available;
    setAvailable(next);
    try {
      await rpc("driver_set_availability", { p_available: next });
      await refreshProfile();
      toast.success(next ? "Estás disponible" : "No disponible");
      load();
    } catch (e) {
      setAvailable(!next);
      toast.error(e?.message || "Error");
    }
  };

  const act = async (fn, params, msg) => {
    setBusy(true);
    try {
      await rpc(fn, params);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <Loader />;

  if (!approved) {
    return (
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-white" data-testid="driver-dashboard">Panel del conductor</h1>
        <Card className="mt-6 border-amber-500/40 bg-amber-500/5" >
          <div className="flex items-center gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-amber-500/15 text-amber-400"><Clock size={24} /></div>
            <div>
              <div className="font-heading text-lg font-bold text-white">
                {profile?.driver_approval === "SUSPENDED" ? "Cuenta suspendida" : "Cuenta pendiente de aprobación"}
              </div>
              <p className="text-sm text-gray-400">
                Un administrador debe aprobar tu cuenta antes de que puedas recibir servicios.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const cs = current?.service;
  const todayStr = new Date().toISOString().slice(0, 10);
  const todays = (earnings?.services || []).filter((s) => (s.delivered_at || "").slice(0, 10) === todayStr);
  const dayEarning = todays.reduce((a, s) => a + Number(s.driver_earning || 0), 0);

  // Navegación externa (Google Maps) según el estado del servicio.
  // Usa las coordenadas reales guardadas al crear el servicio (nunca texto ni mocks).
  const navTarget = cs
    ? ["ACCEPTED", "DRIVER_GOING_TO_PICKUP"].includes(cs.status) && cs.pickup_lat != null && cs.pickup_lng != null
      ? { label: "Ir al origen", lat: cs.pickup_lat, lng: cs.pickup_lng, testId: "navigate-to-pickup-button" }
      : ["PICKED_UP", "IN_TRANSIT"].includes(cs.status) && cs.dropoff_lat != null && cs.dropoff_lng != null
      ? { label: "Ir al destino", lat: cs.dropoff_lat, lng: cs.dropoff_lng, testId: "navigate-to-dropoff-button" }
      : null
    : null;
  const gmapsUrl = navTarget
    ? `https://www.google.com/maps/dir/?api=1&destination=${navTarget.lat},${navTarget.lng}`
    : null;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-heading text-2xl font-extrabold text-white sm:text-3xl" data-testid="driver-dashboard">Panel del conductor</h1>
        <button
          onClick={toggle}
          data-testid="driver-availability-switch"
          className={`flex items-center gap-2.5 rounded-xl border px-4 py-2.5 font-bold transition-all ${
            available ? "border-emerald-500 bg-emerald-500/15 text-emerald-400" : "border-[#27272A] bg-[#18181B] text-gray-400"
          }`}
        >
          <Power size={18} /> {available ? "Disponible" : "No disponible"}
        </button>
      </div>

      {/* Day summary */}
      <div className="mb-6 grid grid-cols-3 gap-3" data-testid="driver-day-summary">
        <div className="rounded-2xl border border-[#27272A] bg-[#141416] p-3.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            <Wallet size={13} className="text-[#FF6A00]" /> Hoy
          </div>
          <div className="mt-1 font-heading text-lg font-extrabold text-[#FF6A00] sm:text-xl" data-testid="driver-day-earning">
            {formatCOP(dayEarning)}
          </div>
        </div>
        <div className="rounded-2xl border border-[#27272A] bg-[#141416] p-3.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            <PackageCheck size={13} className="text-emerald-400" /> Entregas hoy
          </div>
          <div className="mt-1 font-heading text-lg font-extrabold text-white sm:text-xl">{todays.length}</div>
        </div>
        <div className="rounded-2xl border border-[#27272A] bg-[#141416] p-3.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            <Bike size={13} className="text-gray-300" /> Total
          </div>
          <div className="mt-1 font-heading text-lg font-extrabold text-white sm:text-xl">
            {formatCOP(earnings?.total_earning)}
          </div>
        </div>
      </div>

      {/* Current service */}
      {cs ? (
        <Card className="mb-6" data-testid="driver-current-service">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-base font-bold text-white">Servicio actual</h3>
            <StatusBadge status={cs.status} />
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-1">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 text-[#FF6A00]" />
                <div>
                  <div className="text-sm font-semibold text-white">{cs.pickup_address}</div>
                  <div className="text-xs text-gray-500">{cs.pickup_city} · {cs.pickup_reference || "—"}</div>
                </div>
              </div>
              <div className="ml-[8px] h-5 w-0.5 bg-[#27272A]" />
              <div className="flex items-start gap-3">
                <Flag size={18} className="mt-0.5 text-emerald-400" />
                <div>
                  <div className="text-sm font-semibold text-white">{cs.dropoff_address}</div>
                  <div className="text-xs text-gray-500">{cs.dropoff_city} · {cs.dropoff_reference || "—"}</div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-[#0d0d0f] px-3 py-1.5 text-gray-300"><Package size={12} className="mr-1 inline" />{cs.package_type} · {cs.package_weight_kg || "?"} kg</span>
                <span className="rounded-full bg-[#0d0d0f] px-3 py-1.5 text-gray-300"><Phone size={12} className="mr-1 inline" />{cs.recipient_name} · {cs.recipient_phone}</span>
                <span className="rounded-full bg-emerald-500/10 px-3 py-1.5 font-bold text-emerald-400">Ganas {formatCOP(cs.driver_earning)}</span>
              </div>
            </div>

            <div>
              <StatusTimeline history={current.history} currentStatus={cs.status} />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-5 border-t border-[#1e1e21] pt-5">
            {navTarget && (
              <a
                href={gmapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={navTarget.testId}
                className="mb-3 flex w-full items-center justify-center gap-2.5 rounded-xl border-2 border-[#FF6A00] bg-[#FF6A00]/10 py-4 font-heading text-base font-extrabold text-[#FF6A00] transition-all active:scale-95"
              >
                <Navigation size={20} /> {navTarget.label} →
              </a>
            )}

            {NEXT[cs.status] && (
              <button
                onClick={() => act("advance_status", { p_service_id: cs.id, p_new_status: NEXT[cs.status].to }, "Estado actualizado")}
                disabled={busy}
                data-testid="advance-status-button"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF6A00] py-3.5 font-extrabold text-black transition-transform active:scale-95 disabled:opacity-60"
              >
                {NEXT[cs.status].label} <ArrowRight size={18} />
              </button>
            )}

            {cs.status === "DRIVER_ARRIVED_DESTINATION" && (
              <div className="rounded-xl border border-[#FF6A00]/40 bg-[#FF6A00]/5 p-4" data-testid="delivery-code-panel">
                <div className="mb-3 flex items-center gap-2 font-heading font-bold text-white">
                  <KeyRound size={18} className="text-[#FF6A00]" /> Validar entrega
                </div>
                <p className="mb-3 text-xs text-gray-400">Pide el código de 4 dígitos a quien recibe.</p>
                <div className="flex gap-3">
                  <input
                    ref={codeRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="0000"
                    data-testid="delivery-code-input"
                    className="w-32 rounded-xl border-2 border-[#FF6A00] bg-[#0d0d0f] py-3 text-center font-heading text-2xl font-black tracking-[0.4em] text-white outline-none"
                  />
                  <button
                    onClick={() => act("verify_delivery_and_complete", { p_service_id: cs.id, p_code: code }, "¡Entrega confirmada!").then(() => setCode(""))}
                    disabled={busy || code.length !== 4}
                    data-testid="confirm-delivery-button"
                    className="grow rounded-xl bg-emerald-500 font-extrabold text-black transition-transform active:scale-95 disabled:opacity-50"
                  >
                    Confirmar entrega
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={() => act("driver_release_service", { p_service_id: cs.id, p_reason: "Liberado por el conductor" }, "Servicio liberado")}
              disabled={busy}
              data-testid="release-service-button"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-red-500/10 py-2.5 text-sm font-semibold text-red-400 hover:bg-red-500/20"
            >
              <ReleaseIcon size={15} /> Liberar servicio
            </button>
          </div>
        </Card>
      ) : (
        <>
          {/* Offers */}
          <h3 className="mb-4 font-heading text-lg font-bold text-white">
            Ofertas entrantes {offers.length > 0 && <span className="text-[#FF6A00]">({offers.length})</span>}
          </h3>
          {offers.length === 0 ? (
            <Card className="text-center" data-testid="no-offers">
              <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-[#18181B] text-[#FF6A00]"><Bike size={26} /></div>
              <div className="font-heading font-bold text-white">
                {available ? "Esperando ofertas de envío…" : "Actívate como disponible para recibir ofertas"}
              </div>
              <p className="mt-1 text-sm text-gray-500">Las nuevas solicitudes aparecerán aquí automáticamente.</p>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {offers.map((o) => (
                <Card key={o.offer_id} className="border-[#FF6A00]/40" data-testid={`offer-${o.offer_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-heading text-2xl font-black text-emerald-400">{formatCOP(o.driver_earning)}</div>
                      <div className="text-xs text-gray-500">{formatKm(o.estimated_distance_km)} vial</div>
                    </div>
                    <Countdown expiresAt={o.expires_at} />
                  </div>
                  <div className="mt-4 space-y-1">
                    <div className="flex items-start gap-2.5">
                      <MapPin size={15} className="mt-0.5 text-[#FF6A00]" />
                      <div className="text-sm text-white">{o.pickup_address}<span className="text-gray-500"> · {o.pickup_city}</span></div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Flag size={15} className="mt-0.5 text-emerald-400" />
                      <div className="text-sm text-white">{o.dropoff_address}<span className="text-gray-500"> · {o.dropoff_city}</span></div>
                    </div>
                  </div>
                  {/* Detalle de carga visible ANTES de aceptar */}
                  <div className="mt-4 rounded-xl border border-[#27272A] bg-[#0d0d0f] p-3" data-testid={`offer-cargo-${o.offer_id}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400">
                        <Package size={13} className="text-[#FF6A00]" /> Carga
                      </div>
                      {o.is_heavy && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-500 bg-amber-500/15 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-amber-400" data-testid={`offer-heavy-badge-${o.offer_id}`}>
                          <AlertTriangle size={11} /> Carga pesada
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm text-gray-200">
                      <div><span className="text-gray-500">Categoría:</span> {CARGO_CATEGORY_LABELS[o.package_category] || o.package_type || "—"}</div>
                      <div><span className="text-gray-500">Cantidad:</span> {o.package_count ?? "—"}</div>
                      <div><span className="text-gray-500">Peso:</span> {o.package_weight_kg != null ? `${o.package_weight_kg} kg` : "—"}</div>
                      <div><span className="text-gray-500">Dim.:</span> {o.package_dimensions || "—"}</div>
                    </div>
                    {o.package_notes && (
                      <div className="mt-2 border-t border-[#1e1e21] pt-2 text-xs text-gray-400">{o.package_notes}</div>
                    )}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => act("accept_offer", { p_offer_id: o.offer_id }, "¡Servicio aceptado!")}
                      disabled={busy}
                      data-testid="driver-offer-accept-button"
                      className="flex grow items-center justify-center gap-1.5 rounded-xl bg-[#FF6A00] py-3 font-extrabold text-black transition-transform active:scale-95 disabled:opacity-60"
                    >
                      <Check size={17} /> Aceptar
                    </button>
                    <button
                      onClick={() => act("reject_offer", { p_offer_id: o.offer_id }, "Oferta rechazada")}
                      disabled={busy}
                      data-testid="driver-offer-reject-button"
                      className="grid w-12 place-items-center rounded-xl bg-[#18181B] text-gray-400 hover:text-red-400"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
